import React, {useRef} from 'react';
import {ResponsiveContainer as RResponsiveContainer} from 'recharts';
import {usePrintContext} from 'contexts/print-context';
import {Box} from '@mui/material';
import {useDeepMemo} from 'hooks/utils';
import clsx from 'clsx';

ResponsiveContainer.propTypes = {};

// Debounces recharts' internal resize handler so a burst of ResizeObserver
// notifications (e.g. during a window drag) can't trigger a `setState` on
// every single notification within the same frame -- the root cause of the
// "ResizeObserver loop completed with undelivered notifications" browser
// message. This is recharts' own supported mitigation for that issue
// (see node_modules/recharts/lib/component/ResponsiveContainer.js). WHL-265.
const DEFAULT_RESIZE_DEBOUNCE_MS = 200;

function useClientRect(props) {
  const {minWidth = 200, minHeight = 200, maxHeight, maxWidth} = props;
  const ref = useRef();

  const clientRect = ref.current?.getBoundingClientRect() ?? {};

  const {width, height} = useDeepMemo(() => {
    const height = clientRect.height < minHeight ? minHeight : clientRect.height;
    const width = clientRect.width < minWidth ? minWidth : clientRect.width;

    return {
      width: maxWidth ? (width > maxWidth ? maxWidth : width) : width,
      height: maxHeight ? (height > maxHeight ? maxHeight : height) : height,
    };
  }, [clientRect, minHeight, minWidth, maxHeight]);

  return {width: width, height: height, ref};
}

function ResponsiveContainer(props) {
  const {
    className,
    children,
    aspect,
    minWidth,
    minHeight,
    maxHeight,
    maxWidth,
    isAnimationActive,
    debounce = DEFAULT_RESIZE_DEBOUNCE_MS,
    ...rest
  } = props;

  const {printing} = usePrintContext();
  const {width, height, ref: boxRef} = useClientRect({minWidth, minHeight, maxHeight, maxWidth});

  if (printing) {
    return (
      <Box className={clsx('printing-container', className)} width="99%" height="100%" ref={boxRef} {...rest}>
        {React.cloneElement(children, {width, height})}
      </Box>
    );
  }

  return (
    <RResponsiveContainer {...props} debounce={debounce}>
      {children}
    </RResponsiveContainer>
  );
}

export default ResponsiveContainer;
